import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { getMunicipalities } from "../apis/municipalities";
import { parseMunicipalityHomeFromSearch } from "../util/municipality-context";

type MunicipalityHomeContextType = {
  codIbgeMunicipio?: number;
  municipalityName?: string;
  bannerUrl?: string | null;
  bannerLoading: boolean;
  isMunicipalityHome: boolean;
  municipioQuerySuffix: string;
};

const MunicipalityHomeContext = createContext<MunicipalityHomeContextType>({
  codIbgeMunicipio: undefined,
  municipalityName: undefined,
  bannerUrl: null,
  bannerLoading: false,
  isMunicipalityHome: false,
  municipioQuerySuffix: "",
});

const bannerCache = new Map<number, string | null>();
const preloadLinkMap = new Map<
  number,
  { preload?: HTMLLinkElement; preconnect?: HTMLLinkElement }
>();

export const MunicipalityHomeProvider: React.FC = ({ children }) => {
  const location = useLocation<{
    bannerUrl?: string | null;
  }>();

  const {
    codIbgeMunicipio,
    municipalityName,
    isMunicipalityHome,
    municipioQuerySuffix,
  } = useMemo(
    () => parseMunicipalityHomeFromSearch(location.search),
    [location.search]
  );

  const cachedUrl =
    codIbgeMunicipio !== undefined && bannerCache.has(codIbgeMunicipio)
      ? bannerCache.get(codIbgeMunicipio)!
      : null;
  const hasCached =
    codIbgeMunicipio !== undefined && bannerCache.has(codIbgeMunicipio);

  const [bannerUrl, setBannerUrl] = useState<string | null>(
    hasCached ? cachedUrl : null
  );
  const [bannerLoading, setBannerLoading] = useState<boolean>(
    isMunicipalityHome && !hasCached
  );

  useEffect(() => {
    if (!isMunicipalityHome || !codIbgeMunicipio || !municipalityName) {
      setBannerUrl(null);
      setBannerLoading(false);
      return;
    }

    if (codIbgeMunicipio && bannerCache.has(codIbgeMunicipio)) {
      setBannerUrl(bannerCache.get(codIbgeMunicipio) ?? null);
      setBannerLoading(false);
      return;
    }

    let mounted = true;
    const fetchBanner = async () => {
      setBannerLoading(true);
      try {
        const res = await getMunicipalities({
          ibgeCode: codIbgeMunicipio,
          perPage: 1,
        });
        const items = res.data ?? [];
        if (!items || items.length === 0) {
          console.debug(
            `[municipality-home-provider] nenhum item retornado por ibgeCode ${codIbgeMunicipio}`
          );
        }
        const match =
          items.find((item) => item.ibge_code === codIbgeMunicipio) ?? items[0];
        let url = match?.parameters?.banner_url ?? null;

        if (!url) {
          const res2 = await getMunicipalities({
            search: municipalityName,
            perPage: 10,
          });
          const items2 = res2.data ?? [];
          if (!items2 || items2.length === 0) {
            console.debug(
              `[municipality-home-provider] fallback por nome não retornou items para ${municipalityName}`
            );
          }
          const match2 = items2.find(
            (item) => item.ibge_code === codIbgeMunicipio
          );
          if (!match2) {
            console.debug(
              `[municipality-home-provider] fallback não encontrou ibge ${codIbgeMunicipio} na lista por nome`,
              items2.map((i) => i.ibge_code)
            );
          }
          url = match2?.parameters?.banner_url ?? null;
        }

        if (codIbgeMunicipio) {
          bannerCache.set(codIbgeMunicipio, url);

          if (url) {
            const img = new Image();
            img.src = url;

            if (typeof document !== "undefined" && codIbgeMunicipio) {
              try {
                const existing = preloadLinkMap.get(codIbgeMunicipio);
                if (existing) {
                  if (existing.preload && existing.preload.parentNode)
                    existing.preload.parentNode.removeChild(existing.preload);
                  if (existing.preconnect && existing.preconnect.parentNode)
                    existing.preconnect.parentNode.removeChild(
                      existing.preconnect
                    );
                  preloadLinkMap.delete(codIbgeMunicipio);
                }

                const origin = new URL(url).origin;
                const preconnect = document.createElement("link");
                preconnect.rel = "preconnect";
                preconnect.href = origin;
                preconnect.crossOrigin = "";
                document.head.appendChild(preconnect);

                const preload = document.createElement("link");
                preload.rel = "preload";
                preload.as = "image" as any;
                preload.href = url;
                document.head.appendChild(preload);

                preloadLinkMap.set(codIbgeMunicipio, { preload, preconnect });
              } catch (e) {
                console.debug("preload banner failed", e);
              }
            }
          }
        }

        if (mounted && url) {
          const img = new Image();
          img.src = url;
        }

        if (mounted) setBannerUrl(url);
      } catch (e) {
        if (codIbgeMunicipio) bannerCache.set(codIbgeMunicipio, null);
        if (mounted) setBannerUrl(null);
      } finally {
        if (mounted) setBannerLoading(false);
      }
    };

    fetchBanner();
    return () => {
      mounted = false;
    };
  }, [codIbgeMunicipio, municipalityName, isMunicipalityHome]);

  const value = useMemo(
    () => ({
      codIbgeMunicipio,
      municipalityName,
      bannerUrl,
      bannerLoading,
      isMunicipalityHome,
      municipioQuerySuffix,
    }),
    [
      codIbgeMunicipio,
      municipalityName,
      bannerUrl,
      bannerLoading,
      isMunicipalityHome,
      municipioQuerySuffix,
    ]
  );

  return (
    <MunicipalityHomeContext.Provider value={value}>
      {children}
    </MunicipalityHomeContext.Provider>
  );
};

export function useMunicipalityHome() {
  return useContext(MunicipalityHomeContext);
}
